import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  useUpdateTask,
  useDeleteTask,
  getListTasksQueryKey,
  type Task,
} from "@workspace/api-client-react";
import { cn } from "@/lib/utils";

type Unit = "day" | "week" | "month" | "year";
type BasedOn = "scheduled" | "completed";

const PRIORITIES = ["none", "low", "medium", "high"] as const;
const UNITS = ["day", "week", "month", "year"] as const;
const REPEATS: { value: Task["repeat"]; label: string }[] = [
  { value: "none", label: "None" },
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "weekday", label: "Weekdays" },
  { value: "monthly", label: "Every month" },
  { value: "yearly", label: "Every year" },
  { value: "custom", label: "Custom" },
];

const isUnit = (v: unknown): v is Unit =>
  v === "day" || v === "week" || v === "month" || v === "year";

const pill = (active: boolean) =>
  cn(
    "rounded-full px-4 py-2 text-sm font-medium",
    active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
  );

export function TaskEditSheet({
  task,
  open,
  onOpenChange,
}: {
  task: Task;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const due = task.dueAt ? new Date(task.dueAt) : null;
  const [title, setTitle] = React.useState(task.title);
  const [dueDate, setDueDate] = React.useState(
    due ? format(due, "yyyy-MM-dd") : "",
  );
  const [dueTime, setDueTime] = React.useState(due ? format(due, "HH:mm") : "");
  const [priority, setPriority] = React.useState<Task["priority"]>(
    task.priority,
  );
  const [repeat, setRepeat] = React.useState<Task["repeat"]>(task.repeat);
  const [customInterval, setCustomInterval] = React.useState(
    task.repeatInterval ?? 1,
  );
  const [customUnit, setCustomUnit] = React.useState<Unit>(
    isUnit(task.repeatUnit) ? task.repeatUnit : "day",
  );
  const [customBasedOn, setCustomBasedOn] = React.useState<BasedOn>(
    task.repeatBasedOn === "completed" ? "completed" : "scheduled",
  );
  const [endsOn, setEndsOn] = React.useState(!!task.repeatEndsAt);
  const [endsAt, setEndsAt] = React.useState(
    task.repeatEndsAt ? format(new Date(task.repeatEndsAt), "yyyy-MM-dd") : "",
  );
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  React.useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const canSave =
    title.trim().length > 0 &&
    !(repeat === "custom" && endsOn && !endsAt) &&
    !updateTask.isPending;

  const handleSave = () => {
    let dueAt: string | null = null;
    if (dueDate) {
      const d = new Date(`${dueDate}T${dueTime || "09:00"}`);
      dueAt = Number.isNaN(d.getTime()) ? null : d.toISOString();
    }
    const isCustom = repeat === "custom";
    updateTask.mutate(
      {
        id: task.id,
        data: {
          title: title.trim(),
          dueAt,
          priority,
          repeat,
          repeatInterval: isCustom ? customInterval : null,
          repeatUnit: isCustom ? customUnit : null,
          repeatBasedOn: isCustom ? customBasedOn : null,
          repeatEndsAt:
            isCustom && endsOn && endsAt
              ? new Date(endsAt + "T23:59:59").toISOString()
              : null,
        },
      },
      {
        onSuccess: (updated) => {
          queryClient.setQueryData<Task[]>(getListTasksQueryKey(), (list) =>
            list?.map((t) => (t.id === updated.id ? updated : t)),
          );
          onOpenChange(false);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteTask.mutate(
      { id: task.id },
      {
        onSuccess: () => {
          queryClient.setQueryData<Task[]>(getListTasksQueryKey(), (list) =>
            list?.filter((t) => t.id !== task.id),
          );
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl px-5 pb-6 pt-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="space-y-5">
          <h2 className="text-lg font-semibold">Edit task</h2>

          <div className="space-y-2">
            <div className="text-sm font-medium">Title</div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-xl bg-muted px-4 py-3 text-base"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Due date and time</div>
              {dueDate && (
                <button
                  type="button"
                  onClick={() => {
                    setDueDate("");
                    setDueTime("");
                  }}
                  className="text-xs font-medium text-muted-foreground"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="min-w-0 flex-1 rounded-xl bg-muted px-4 py-3 text-base"
              />
              <input
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="w-32 rounded-xl bg-muted px-3 py-3 text-base"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Priority</div>
            <div className="flex flex-wrap gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  className={cn(pill(priority === p), "capitalize")}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Repeat</div>
            <div className="flex flex-wrap gap-2">
              {REPEATS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRepeat(r.value)}
                  className={pill(repeat === r.value)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {repeat === "custom" && (
            <div className="space-y-5 rounded-2xl bg-muted/40 p-4">
              <div className="space-y-2">
                <div className="text-sm font-medium">Based on</div>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      ["scheduled", "Scheduled date"],
                      ["completed", "Completed date"],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setCustomBasedOn(v)}
                      className={pill(customBasedOn === v)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm font-medium">Every</div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setCustomInterval((n) => Math.max(1, n - 1))}
                    className="h-10 w-10 rounded-full bg-muted text-xl leading-none"
                    aria-label="Decrease"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={customInterval}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      setCustomInterval(
                        Number.isNaN(n) ? 1 : Math.min(999, Math.max(1, n)),
                      );
                    }}
                    className="h-10 w-16 rounded-lg bg-muted text-center text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setCustomInterval((n) => Math.min(999, n + 1))}
                    className="h-10 w-10 rounded-full bg-muted text-xl leading-none"
                    aria-label="Increase"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {UNITS.map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setCustomUnit(u)}
                      className={cn(pill(customUnit === u), "capitalize")}
                    >
                      {customInterval === 1 ? u : u + "s"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Ends</div>
                <button
                  type="button"
                  onClick={() => setEndsOn(false)}
                  className={cn(
                    "w-full rounded-xl bg-muted/60 px-4 py-3 text-left text-sm font-medium",
                    !endsOn && "bg-primary/10 text-primary",
                  )}
                >
                  Never
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEndsOn(true);
                    if (!endsAt) {
                      setEndsAt(dueDate || format(new Date(), "yyyy-MM-dd"));
                    }
                  }}
                  className={cn(
                    "w-full rounded-xl bg-muted/60 px-4 py-3 text-left text-sm font-medium",
                    endsOn && "bg-primary/10 text-primary",
                  )}
                >
                  On date (inclusive)
                </button>
                {endsOn && (
                  <input
                    type="date"
                    value={endsAt}
                    min={dueDate || undefined}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className="w-full rounded-xl bg-muted px-4 py-3 text-base"
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={!canSave} onClick={handleSave}>
              {updateTask.isPending ? "Saving..." : "Save"}
            </Button>
          </div>

          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteTask.isPending}
            className={cn(
              "w-full rounded-xl py-3 text-sm font-medium",
              confirmDelete
                ? "bg-destructive text-destructive-foreground"
                : "bg-muted/60 text-destructive",
            )}
          >
            {confirmDelete ? "Tap again to delete" : "Delete task"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
